import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Dropdown,
  Input,
  RadioButton,
} from 'ncoded-component-library';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useCallbackRef from 'hooks/useCallbackRef';
import useMatchMedia from 'hooks/useMatchMedia';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import ArrowupIcon from 'icons/Arrowup.icon';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import { FilterFieldValue, FilterOption } from 'models/Filters';
import useQueryParams from 'hooks/useQueryParams';
import utils, {
  getSortValue,
  getIsSortApplied,
  getIsSortedDescBy,
  getHasElementsInArray,
  getResetedPageParams,
} from 'utils';
import { LIMIT_PARAM_KEYWORD, PAGE_PARAM_KEYWORD } from 'data/global';
import SearchIcon from 'icons/Search.icon';
import { SORTING_ORDER, SortingDirectionValue } from 'models/Sort';

import './ColumnSortFilterDropdown.styles.scss';

type ColumnSortFilterDropdownProps = {
  className?: string;
  isDisabled?: boolean;
  sort?: string;
  sortingValue?: string;
  filterOption?: FilterOption;
  hasCheckboxAll: boolean;
  sortDirectionTranslation?: SortingDirectionValue;
  showSearch?: boolean;
  pageParam?: string | string[];
  updateSort: (value: string) => void;
};

const ColumnSortFilterDropdown: React.FC<ColumnSortFilterDropdownProps> = (
  props,
) => {
  const {
    children,
    className,
    isDisabled,
    sort,
    sortingValue,
    filterOption,
    hasCheckboxAll = false,
    sortDirectionTranslation,
    showSearch = false,
    pageParam = PAGE_PARAM_KEYWORD,
  } = props;

  const { params, replaceMultipleQueryParams, removeQueryParam } =
    useQueryParams();

  const [filterValue, setFilterValue] = useState<FilterFieldValue>(null);
  const [searchValue, setSearchValue] = useState('');
  const [localSort, setLocalSort] = useState(sort ?? '');

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();
  const filterOptionRef = useRef(filterOption);
  const paramsRef = useRef(params);
  const sortRef = useRef(sort);
  const isPhablet = useMatchMedia('isPhablet');

  filterOptionRef.current = filterOption;
  paramsRef.current = params;
  sortRef.current = sort;

  const isOpen = dropdown?.isOpen;

  const { t } = useTranslation();

  // Runs only when the dropdown opens to initialise sort and filter values from URL params.
  // Refs give access to latest values without causing re-initialisation on every params change.
  useEffect(() => {
    if (!isOpen) return;

    setLocalSort(sortRef.current ?? '');

    const option = filterOptionRef.current;
    if (!option) return;

    const { isMultiple, value, getInitialValue } = option;

    const currentParams = paramsRef.current;
    const currentValue = getInitialValue
      ? getInitialValue(currentParams)
      : isMultiple
      ? utils.mapParamsToArray(currentParams[value])
      : currentParams[value] || null;

    setFilterValue(currentValue);
    setSearchValue('');
  }, [isOpen]);

  const handleSort = useCallback(
    (isDesc: boolean) => {
      setLocalSort(getSortValue(sortingValue, isDesc));
    },
    [sortingValue],
  );

  const handleApply = useCallback(() => {
    const newParams: Record<string, string | number | null> = {
      ...getResetedPageParams(pageParam),
    };

    const limitValue =
      paramsRef.current[LIMIT_PARAM_KEYWORD as keyof typeof params];
    if (limitValue) {
      newParams[LIMIT_PARAM_KEYWORD] = limitValue;
    }

    if (sortingValue && localSort) {
      newParams['sort'] = localSort;
    }

    if (filterOption) {
      const hasFilterValue = Array.isArray(filterValue)
        ? filterValue.length > 0
        : filterValue !== null && filterValue !== undefined;

      if (hasFilterValue) {
        if (filterOption.apply) {
          Object.assign(newParams, filterOption.apply(filterValue));
        } else {
          const value = Array.isArray(filterValue)
            ? utils.mapArrayToParams(
                utils.multiSelectValueToStringArray(filterValue as any),
              )
            : (filterValue as string);
          newParams[filterOption.value] = value;
        }
      }
    }

    replaceMultipleQueryParams(newParams as any);
    dropdown?.setIsOpen(false);
  }, [
    sortingValue,
    localSort,
    filterOption,
    filterValue,
    pageParam,
    replaceMultipleQueryParams,
    dropdown,
  ]);

  const handleClearFilter = useCallback(() => {
    if (!filterOption) return;
    removeQueryParam(filterOption.value as any);
    filterOption.clearFields?.forEach((field) =>
      removeQueryParam(field as any),
    );
    setFilterValue(null);
    dropdown?.setIsOpen(false);
  }, [filterOption, removeQueryParam, dropdown]);

  const handleCheckboxChange = useCallback((optionValue: string) => {
    setFilterValue((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      return prevArray.includes(optionValue)
        ? prevArray.filter((v) => v !== optionValue)
        : [...prevArray, optionValue];
    });
  }, []);

  const handleRadioChange = useCallback((optionValue: string) => {
    setFilterValue((prev) => (prev === optionValue ? null : optionValue));
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!filterOption?.options) return;
    const allValues = filterOption.options.map(({ value }) => value);
    setFilterValue((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      return prevArray.length === allValues.length ? [] : allValues;
    });
  }, [filterOption]);

  const isSortActive = getIsSortApplied(localSort, sortingValue);
  const isSortedDesc = getIsSortedDescBy(localSort, sortingValue);

  const filteredOptions = filterOption?.options?.filter((option) =>
    option.label.toLowerCase().includes(searchValue.toLowerCase()),
  );

  const selectedValues = Array.isArray(filterValue) ? filterValue : [];

  const selectedValue = typeof filterValue === 'string' ? filterValue : null;

  const allSelected =
    getHasElementsInArray(filteredOptions) &&
    filteredOptions.every(({ value }) => selectedValues.includes(value));

  const classes = classNames('column-sort-filter-dropdown', className);

  const getSortButton = useCallback(
    (isDesc: boolean) => (
      <Button
        className={classNames('sort-option', {
          active:
            isSortActive &&
            ((isDesc && isSortedDesc) || (!isDesc && !isSortedDesc)),
        })}
        icon={isDesc ? ArrowdownIcon : ArrowupIcon}
        onClick={() => handleSort(isDesc)}
      >
        <span>
          {isDesc
            ? sortDirectionTranslation?.[SORTING_ORDER.Z_TO_A] ||
              t('descending')
            : sortDirectionTranslation?.[SORTING_ORDER.A_TO_Z] ||
              t('ascending')}
        </span>
      </Button>
    ),
    [handleSort, isSortActive, isSortedDesc, sortDirectionTranslation, t],
  );

  return (
    <Dropdown
      ref={dropdownRef}
      className={classes}
      portalClassName="column-sort-filter-dropdown__portal"
      renderAsPortal={!isPhablet}
      disabled={isDisabled}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      trigger={
        <div
          className={classNames({
            open: isOpen,
            disabled: isDisabled,
          })}
        >
          {children}
          <ChevrondownIcon
            className={classNames('chevron-icon', { rotated: isOpen })}
          />
        </div>
      }
    >
      <div className="dropdown-panel">
        {sortingValue && (
          <div className="sort-section">
            <p className="section-label">{t('sort')}</p>
            {getSortButton(false)}
            {getSortButton(true)}
          </div>
        )}

        {filterOption && (
          <div className="filter-section">
            <p className="section-label">{t('filter')}</p>

            {filterOption.render ? (
              filterOption.render({
                value: filterValue,
                onChange: setFilterValue,
              })
            ) : (
              <div className="main-section">
                {showSearch && (
                  <div className="search-wrapper">
                    <Input
                      prefixNode={<SearchIcon />}
                      placeholder={`${t('search')}...`}
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                    />
                  </div>
                )}

                {hasCheckboxAll && (
                  <Checkbox
                    label={t('selectAll')}
                    className="select-all"
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />
                )}

                <div className="checkboxes-list">
                  {filteredOptions?.map(({ value, label }) =>
                    filterOption.isMultiple ? (
                      <Checkbox
                        key={value}
                        label={label}
                        checked={selectedValues.includes(value)}
                        onChange={() => handleCheckboxChange(value)}
                      />
                    ) : (
                      <RadioButton
                        key={value}
                        label={label}
                        checked={selectedValue === value}
                        value={value}
                        name={filterOption.value}
                        onChange={() => handleRadioChange(value)}
                      />
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {(sortingValue || filterOption) && (
          <div className="filter-actions">
            {filterOption && (
              <Button variant="link" onClick={handleClearFilter}>
                {t('clearFilters')}
              </Button>
            )}
            <Button onClick={handleApply}>{t('apply')}</Button>
          </div>
        )}
      </div>
    </Dropdown>
  );
};

export default ColumnSortFilterDropdown;
